/* eslint-disable no-console */
/**
 * Client-side Logger wrapping @sentry/react
 *
 * Same API as the server Logger (@shared/server/logger) but imports
 * from @sentry/react instead of @sentry/cloudflare.
 *
 * Usage:
 *   import { Logger, ConfiguredLogger } from "@/lib/logger";
 *
 *   Logger.error("render failed", { component: "BlockView" });
 *
 *   const logger = new ConfiguredLogger({ caller: "MemoryStore" });
 *   logger.info("memory added", { memoryId });
 */
import { withScope, captureMessage, captureException, } from "@sentry/react";
const DEFAULT_OPTIONS = {
    sendToSentry: true,
};
const SENTRY_SEVERITY = {
    debug: "debug",
    info: "info",
    warn: "warning",
    error: "error",
};
export class ConfiguredLogger {
    _defaults;
    constructor(defaults) {
        this._defaults = defaults;
    }
    debug(message, meta, options) {
        Logger.debug(message, meta, { ...this._defaults, ...options });
    }
    info(message, meta, options) {
        Logger.info(message, meta, { ...this._defaults, ...options });
    }
    warn(message, meta, options) {
        Logger.warn(message, meta, { ...this._defaults, ...options });
    }
    error(message, meta, options) {
        Logger.error(message, meta, { ...this._defaults, ...options });
    }
}
export class Logger {
    static debug(message, meta, options = DEFAULT_OPTIONS) {
        log("debug", message, meta ?? {}, options);
    }
    static info(message, meta, options = DEFAULT_OPTIONS) {
        log("info", message, meta ?? {}, options);
    }
    static warn(message, meta, options = DEFAULT_OPTIONS) {
        log("warn", message, meta ?? {}, options);
    }
    static error(message, meta, options = DEFAULT_OPTIONS) {
        log("error", message, meta ?? {}, options);
    }
    static captureError(error, extras) {
        withScope((scope) => {
            if (extras && Object.keys(extras).length > 0) {
                scope.setContext("extras", extras);
            }
            captureException(error);
        });
    }
}
// ---------------------------------------------------------------------------
// Internal
// ---------------------------------------------------------------------------
function log(level, message, meta, options) {
    if (options.caller) {
        message = `[${options.caller}] ${message}`;
    }
    const sendToSentry = options.sendToSentry ?? true;
    if (sendToSentry && (level === "warn" || level === "error")) {
        withScope((scope) => {
            scope.setLevel(SENTRY_SEVERITY[level]);
            if (meta && Object.keys(meta).length > 0) {
                scope.setContext("meta", meta);
            }
            captureMessage(message);
        });
    }
    if (process.env.NODE_ENV === "production" && level === "info")
        return;
    if (meta && Object.keys(meta).length > 0) {
        console[level](message, meta);
    }
    else {
        console[level](message);
    }
}
